import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebaseConfig';
import { reportClientError } from './ErrorReporter';

const functions = getFunctions(app, 'us-central1');
const PENDING_BIND_KEY = 'lumirae_pending_gift_set_bind';

const callFunction = async (name, payload, errorContext) => {
  const callable = httpsCallable(functions, name);
  try {
    const result = await callable(payload);
    return result.data;
  } catch (error) {
    reportClientError(errorContext || `giftSet.${name}`, error);
    throw error;
  }
};

const getAdminToken = (ownerUuid) => sessionStorage.getItem(`admin_token_${ownerUuid}`);

const normalizePendingBind = (value) => {
  if (!value || typeof value !== 'object') return null;
  if (!value.token || !value.ownerUuid || !value.expiresAt) return null;
  return value;
};

export const GiftSetService = {
  pendingBindKey: PENDING_BIND_KEY,

  resolveGiftPage: async (uuid) => {
    return callFunction('resolveGiftPage', { uuid }, 'giftSet.resolveGiftPage');
  },

  beginAddGiftSetMember: async (ownerUuid, groupName) => {
    return callFunction('beginAddGiftSetMember', {
      ownerUuid,
      groupName,
      adminSessionToken: getAdminToken(ownerUuid),
    }, 'giftSet.beginAddMember');
  },

  completeAddGiftSetMember: async ({ memberUuid, bindSessionToken, nickname }) => {
    return callFunction('completeAddGiftSetMember', {
      memberUuid,
      bindSessionToken,
      nickname,
    }, 'giftSet.completeAddMember');
  },

  listGiftSetMembers: async (ownerUuid) => {
    return callFunction('listGiftSetMembers', {
      ownerUuid,
      adminSessionToken: getAdminToken(ownerUuid),
    }, 'giftSet.listMembers');
  },

  removeGiftSetMember: async (ownerUuid, memberUuid) => {
    return callFunction('removeGiftSetMember', {
      ownerUuid,
      memberUuid,
      adminSessionToken: getAdminToken(ownerUuid),
    }, 'giftSet.removeMember');
  },

  updateGiftSetName: async (ownerUuid, name) => {
    return callFunction('updateGiftSetName', {
      ownerUuid,
      name,
      adminSessionToken: getAdminToken(ownerUuid),
    }, 'giftSet.updateName');
  },

  savePendingBind: (session) => {
    const pending = {
      token: session.token,
      ownerUuid: session.ownerUuid,
      groupId: session.groupId,
      groupName: session.groupName || 'Gift Set',
      expiresAt: Number(session.expiresAt || 0),
      addedUuids: [],
      createdAt: Date.now(),
    };
    localStorage.setItem(PENDING_BIND_KEY, JSON.stringify(pending));
    return pending;
  },

  getPendingBind: () => {
    try {
      const pending = normalizePendingBind(JSON.parse(localStorage.getItem(PENDING_BIND_KEY) || 'null'));
      if (!pending) return null;
      if (Number(pending.expiresAt || 0) <= Date.now()) {
        localStorage.removeItem(PENDING_BIND_KEY);
        return null;
      }
      return {
        ...pending,
        addedUuids: Array.isArray(pending.addedUuids) ? pending.addedUuids : [],
      };
    } catch {
      localStorage.removeItem(PENDING_BIND_KEY);
      return null;
    }
  },

  markPendingMemberAdded: (memberUuid) => {
    const pending = GiftSetService.getPendingBind();
    if (!pending) return null;
    const addedUuids = Array.from(new Set([...(pending.addedUuids || []), memberUuid]));
    const next = { ...pending, addedUuids };
    localStorage.setItem(PENDING_BIND_KEY, JSON.stringify(next));
    return next;
  },

  clearPendingBind: () => {
    localStorage.removeItem(PENDING_BIND_KEY);
  },

  formatShortUuid: (uuid = '') => {
    const value = String(uuid || '');
    if (value.length <= 12) return value;
    return `${value.slice(0, 6)}...${value.slice(-4)}`;
  },

  parseUuidFromInput: (input = '') => {
    const value = String(input || '').trim();
    if (!value) return '';
    const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
    try {
      const url = new URL(candidate);
      const segments = url.pathname.split('/').filter(Boolean);
      const pageIndex = segments.findIndex((segment) => segment === 'page' || segment === 'pages');
      const uuid = pageIndex >= 0 ? segments[pageIndex + 1] || '' : segments[0] || '';
      if (uuid) return uuid;
    } catch {
      return value.replace(/^\/+|\/+$/g, '').split(/[/?#]/)[0] || '';
    }
    return value.replace(/^\/+|\/+$/g, '').split(/[/?#]/)[0] || '';
  },
};

export default GiftSetService;
